// src/components/instructor/OrgBookingsCRM.js
//
// Organization Bookings CRM — master booking view for ski school ops.
// Layout (top to bottom):
//   1. Page header + stat strip
//   2. Status legend bar
//   3. Inline season calendar (single month, light theme)
//   4. Two-panel dual schedule (SAS master with AM/PM + Collective)
//      — drag-and-drop booking reassignment via @dnd-kit/core
//   5. Bottom row: Needs attention + Instructor availability

import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useAuth } from '../../AuthContext';
import { db } from '../../firebase';
import { useSelectedOrganizationContext } from '../../utils/organizationSelection';
import { getOrganizationBookings, getOrganizationBookingRequests, getOrganizationAvailability, assignInstructorToBooking } from '../../utils/booking';
import { loadOrganizationMembers } from '../../utils/organizationData';
import { resolveInstructorCategory, CURRENT_SEASON_ID } from '../../utils/instructorCategory';
import { canTeach, isLessonCapacity } from '../../utils/activityModel';
import { isClientBooking, isLiveBooking } from '../../utils/scheduleModel';
import {
  buildDailySchedule,
  computeWeeklyUtilization,
  getBookingChipStatus,
  classifyBookingPeriod,
  CHIP_COLORS,
  fmtTime,
  fmtShortDate,
  fmtDayHeader,
  getTimeWindowBadge,
} from '../../utils/bookingCRM';
import { buildCalendarMonth, monthLabel, shortMonthLabel, utilBarClass } from '../../utils/calendarData';
import { buildWinterAttention } from '../../utils/winterAttention';
import { isGridHold, isCoveredGridHold } from '../../utils/operationsWorkspace';
import { zonedDateKey, zonedDateParts, wallClockToInstant, zonedShiftDays, LEGACY_OPS_TIMEZONE } from '../../utils/opsTime';
import { resolveOrgTimezone } from '../../utils/organizationRuntime';
import { DndContext, DragOverlay, PointerSensor, useSensor, useSensors, useDraggable, useDroppable } from '@dnd-kit/core';

// Pass 59B3 — user-facing chip name for a schedule-grid hold (never a client identity; concise label).
const holdChipName = (b = {}) => b.sourceScheduleLabel || 'Hold';

// ── Design tokens ──────────────────────────────────────────────────────────

const V = {
  surface: '#FFFFFF', s2: '#F2F1EE', s3: '#ECEAE6',
  border: '#E2E0DB', borderStrong: '#C8C5BE',
  t1: '#1A1917', t2: '#6B6860', t3: '#9E9B95',
  green: '#1D9E75', greenBg: '#E1F5EE', greenTxt: '#085041',
  amber: '#C97D10', amberBg: '#FEF3C7', amberTxt: '#92400E',
  blue: '#2563EB', blueBg: '#EFF4FE', blueTxt: '#1A3FA0',
  red: '#DC2626', redBg: '#FEF2F2', redTxt: '#991B1B',
  purple: '#7C3AED', purpleBg: '#F5F3FF', purpleSoft: '#EDE9FE',
  purpleBorder: '#C4B5FD', purpleTxt: '#4C1D95',
  grayBg: '#F4F3F0', grayTxt: '#57534E',
  shadow: '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)',
};
const UTIL_COLORS = { none: 'transparent', low: 'rgba(29,158,117,0.15)', mid: 'rgba(29,158,117,0.35)', high: '#1D9E75', peak: '#16A34A' };
const F = "'DM Sans',sans-serif";
const FM = "'DM Mono',monospace";

// ── Main component ─────────────────────────────────────────────────────────

// Pass 49B §3/§4: the Bookings & Availability design is preserved. When the Operations
// workspace hosts it as the Calendar view it passes season-scoped bookings/members from the
// ONE realtime source (providedBookings/providedMembers) — the component then does NOT open
// its own one-shot query. Standalone, it still self-loads exactly as before.
function OrgBookingsCRM({ onSelectInstructor, providedBookings, providedMembers, providedRequests, providedSlots, selectedSeasonId, currentSeasonId = CURRENT_SEASON_ID, timeZone, initialDate, readOnly = false, onViewDay }) {
  const { userData } = useAuth();
  const { selectedOrganization } = useSelectedOrganizationContext(userData);
  const orgId = selectedOrganization?.organizationId || userData?.orgId || '';
  // Pass 82C.2 §17 — all calendar date maths (selected day, month nav, day click, today highlight, headers) run in the
  // SELECTED ORGANIZATION's timezone (prop wins → org doc → legacy SAS JST), never a hard-coded Asia/Tokyo.
  const tz = timeZone || resolveOrgTimezone(selectedOrganization) || LEGACY_OPS_TIMEZONE;
  const external = providedBookings !== undefined; // hosted by the workspace on realtime data

  const [loading, setLoading] = useState(!external);
  const [bookings, setBookings] = useState(providedBookings || []);
  const [requests, setRequests] = useState(providedRequests || []);
  const [members, setMembers] = useState(providedMembers || []);
  const [slots, setSlots] = useState(providedSlots || []);
  // Pass 49B §1: the operational date is season-aware. When hosted, the initial/selected date
  // comes from the workspace (the season's real range) — never a stale `new Date()` that could
  // show an August-2026 month over 2024/25 data.
  const [selectedDate, setSelectedDate] = useState(initialDate instanceof Date ? initialDate : new Date());

  // §1 reset the calendar's date context whenever the season (its initialDate) changes.
  useEffect(() => {
    if (initialDate instanceof Date) setSelectedDate(initialDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSeasonId, initialDate && initialDate.getTime()]);
  const [showCalDetail, setShowCalDetail] = useState(false);
  const [dragError, setDragError] = useState(null); // { bookingId, message }
  const [blockModal, setBlockModal] = useState(null); // { instructorName, period, existingBooking }
  const [lastReassign, setLastReassign] = useState(null); // { bookingId, fromId, fromName, toId, toName, clientName }
  const undoTimer = useRef(null);

  // §4 the calendar's visible month is the org-timezone month of the selected date.
  const calParts = zonedDateParts(selectedDate, tz) || { year: new Date().getFullYear(), month: 1 };
  const calYear = calParts.year;
  const calMonth = calParts.month - 1; // 0-indexed month for the calendar grid

  const reloadBookings = useCallback(async () => {
    if (external) return; // realtime source refreshes itself
    const bks = await getOrganizationBookings(db, orgId).catch(() => []);
    setBookings(bks || []);
  }, [orgId, external]);

  // Hosted mode: mirror the workspace's realtime, season-scoped data into local state.
  useEffect(() => {
    if (!external) return;
    setBookings(providedBookings || []);
    setMembers(providedMembers || []);
    if (providedRequests !== undefined) setRequests(providedRequests || []);
    if (providedSlots !== undefined) setSlots(providedSlots || []);
    setLoading(false);
  }, [external, providedBookings, providedMembers, providedRequests, providedSlots]);

  // Standalone mode: self-load once (unchanged legacy behaviour).
  useEffect(() => {
    if (external || !orgId) return;
    setLoading(true);
    Promise.all([
      getOrganizationBookings(db, orgId).catch(() => []),
      getOrganizationBookingRequests(db, orgId).catch(() => []),
      loadOrganizationMembers(db, orgId).catch(() => []),
      getOrganizationAvailability(db, orgId).catch(() => []),
    ]).then(([bks, reqs, mems, avSlots]) => {
      setBookings(bks || []); setRequests(reqs || []);
      setMembers(mems || []); setSlots(avSlots || []);
    }).finally(() => setLoading(false));
  }, [orgId, external]);

  // Derived data — §8 season-aware category + §13 weekly windows anchored on the SELECTED
  // operational date (never new Date()).
  // Pass 82D §17 — the winter-core Needs-Attention model (unpaid/unassigned + missing pickup + availability conflict,
  // incl. a Motus-native event colliding with a source booking). Source stale/failed freshness is shown in Sync status.
  const attentionQueue = useMemo(() => buildWinterAttention({ bookings, bookingRequests: requests, members, nowMs: Date.now() }), [bookings, requests, members]);
  const dailySchedule = useMemo(() => buildDailySchedule({ bookings, members, date: selectedDate, seasonId: selectedSeasonId }), [bookings, members, selectedDate, selectedSeasonId]);
  const weeklyUtil = useMemo(() => computeWeeklyUtilization({ bookings, availabilitySlots: slots, members, referenceDate: selectedDate, seasonId: selectedSeasonId }), [bookings, slots, members, selectedDate, selectedSeasonId]);
  const calData = useMemo(() => buildCalendarMonth({ bookings, members, year: calYear, month: calMonth, seasonId: selectedSeasonId }), [bookings, members, calYear, calMonth, selectedSeasonId]);

  // §1/§2 the Calendar lesson panels show LESSON-capable staff only (teaching capability, and
  // confirmed-current for the current season). A current teaching person whose commercial
  // category is unset (e.g. Sam) is NOT dropped — it lands in the "Other current teaching" group.
  // A non-teaching operational person (e.g. Jed the driver) is NOT a lesson-capacity row here,
  // but still appears in the Day/Week operational views.
  const isTeach = (r) => canTeach(r.member || {}, selectedSeasonId);
  const internalSchedule = dailySchedule.filter(r => r.instructorPool === 'internal' && isTeach(r));
  const otherTeachingSchedule = dailySchedule.filter(r => r.instructorId !== '__unassigned__' && r.instructorPool === 'unclassified' && isTeach(r));
  const unassigned = dailySchedule.filter(r => r.instructorId === '__unassigned__');
  // §2 the SAS-master (admin-controlled) teaching capacity = School + current teaching staff
  // whose commercial category is unset (e.g. Sam). Sam is counted here WITHOUT being reclassified
  // as School; commercial School/Collective counts stay separate in the stat strip.
  const sasTeachingSchedule = internalSchedule.concat(otherTeachingSchedule);
  // §1/§2 SAS teaching capacity + utilisation count CLIENT LESSON bookings only, across the full
  // SAS teaching schedule (School + category-not-set teaching staff like Sam).
  const internalBooked = sasTeachingSchedule.filter(r => r.bookings.some(isClientBooking)).length;
  const internalFree = sasTeachingSchedule.length - internalBooked;
  const selectedDayUtilPct = sasTeachingSchedule.length ? Math.round((internalBooked / sasTeachingSchedule.length) * 100) : 0;
  // Pass 61A — the Master Schedule unassigned strip must use the CANONICAL live-client-demand predicate
  // (same truth as buildUnassignedQueue), NOT a weak status filter: retired/superseded bootstrap placeholders
  // (active:false / lifecycleState:'retired_superseded' / rebaselineSuperseded) and non-client entries (RDO,
  // grid holds) must NEVER appear as assignable demand. isLiveBooking excludes retired+cancelled; isClientBooking
  // excludes blocked (holds/RDO); !instructorId keeps it unassigned. On current live data this yields 0.
  const unassignedBookings = useMemo(() => bookings.filter(b => !b.instructorId && isLiveBooking(b) && isClientBooking(b)), [bookings]);

  // Pull-in modal (legacy flow, kept for button)
  const [pullInTarget, setPullInTarget] = useState(null);
  const [pullInBookingId, setPullInBookingId] = useState('');
  const [pullInStatus, setPullInStatus] = useState('');
  const handlePullIn = async () => {
    if (readOnly) return; // §3 historical read-only — refuse the write
    if (!pullInBookingId || !pullInTarget) return;
    // §5 capability gate — a collective pull-in is still a lesson assignment.
    const pullMember = members.find(m => m.userId === pullInTarget.instructorId) || {};
    if (!isLessonCapacity(pullMember, selectedSeasonId)) {
      setPullInStatus(`error:${pullInTarget.instructorName || 'That person'} is not lesson-capable (teaching + confirmed-current required).`);
      return;
    }
    setPullInStatus('loading');
    try {
      await assignInstructorToBooking(db, { bookingId: pullInBookingId, instructorId: pullInTarget.instructorId, instructorName: pullInTarget.instructorName, assignedByUserId: userData?.uid || '', assignedByName: userData?.name || '', userData, assignmentSource: 'collective_pull_in' });
      setPullInStatus('success');
      await reloadBookings();
      setTimeout(() => { setPullInTarget(null); setPullInBookingId(''); setPullInStatus(''); }, 1500);
    } catch (err) { setPullInStatus(`error:${err.message || 'Failed'}`); }
  };

  // Pass 82C.2 §17 — all date navigation is in the org timezone: shift whole org-local days, move by org-local
  // months, and key days by the org calendar — never the operator's browser timezone.
  const shiftDate = (delta) => setSelectedDate(zonedShiftDays(selectedDate, delta, tz));
  const shiftCalMonth = (delta) => {
    const p = zonedDateParts(selectedDate, tz) || { year: calYear, month: calMonth + 1 };
    const total = (p.year * 12 + (p.month - 1)) + delta;
    const y = Math.floor(total / 12); const mo = (total % 12) + 1;
    setSelectedDate(wallClockToInstant(`${y}-${String(mo).padStart(2, '0')}-01`, '12:00', tz));
  };
  const handleCalDayClick = (dateKey) => { const d = wallClockToInstant(dateKey, '12:00', tz); if (d) setSelectedDate(d); };
  const selectedDateKey = zonedDateKey(selectedDate, tz);

  // ── Drag-and-drop ────────────────────────────────────────────────────────

  const [activeDrag, setActiveDrag] = useState(null); // { booking, fromInstructorId, fromPeriod }
  const bookingsBeforeDrag = useRef(null);

  // §3 in historical read-only mode the drag sensor requires an unreachable distance, so a
  // drag can never even start (belt-and-braces with the handler-level refusal below).
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: readOnly ? 100000 : 5 } }));

  // Build member name lookup
  const memberNameMap = useMemo(() => {
    const m = {};
    for (const mem of members) m[mem.userId] = mem.displayName || mem.name || '';
    return m;
  }, [members]);

  const handleDragStart = useCallback((event) => {
    if (readOnly) return; // §3 no drag in historical read-only mode
    const data = event.active.data.current;
    if (!data) return;
    setActiveDrag(data);
    bookingsBeforeDrag.current = bookings;
    setDragError(null);
  }, [bookings, readOnly]);

  const handleDragEnd = useCallback(async (event) => {
    const { active, over } = event;
    const dragData = active?.data?.current;
    setActiveDrag(null);

    if (readOnly) return; // §3 refuse ALL reassignment writes in historical read-only mode
    if (!over || !dragData) return;

    const dropId = over.id;
    const [targetInstructorId, targetPeriod] = String(dropId).split('::');
    if (!targetInstructorId || targetInstructorId === dragData.fromInstructorId) return;

    const { bookingId } = dragData;
    const targetName = memberNameMap[targetInstructorId] || '';
    const targetMember = members.find(m => m.userId === targetInstructorId) || {};
    // §5 CAPABILITY GATE: a client lesson may only go to a lesson-capable target (teaching
    // capability + confirmed-current for the current season). Category alone (School OR
    // Collective) does NOT imply teachable; a driver/roster-review person is refused — no write.
    if (!isLessonCapacity(targetMember, selectedSeasonId)) {
      setDragError({ bookingId, message: `${targetName || 'That person'} can’t take a lesson (needs teaching capability + confirmed-current status).` });
      setTimeout(() => setDragError(null), 4500);
      return; // do NOT call assignInstructorToBooking; chip snaps back
    }
    // Pass 46F: only a CONFIRMED collective member is a collective pull-in target.
    // Unclassified must not silently behave as School (or Collective).
    const isCollectiveTarget = resolveInstructorCategory(targetMember, { season: selectedSeasonId }) === 'collective';

    // Conflict check — does the target already have a booking in this period?
    const targetRow = dailySchedule.find(r => r.instructorId === targetInstructorId);
    if (targetRow && targetPeriod) {
      const conflicting = targetRow.bookings.find(b => {
        if (b.id === bookingId) return false; // don't conflict with self
        const p = classifyBookingPeriod(b);
        if (targetPeriod === 'am' && (p === 'am' || p === 'fd')) return true;
        if (targetPeriod === 'pm' && (p === 'pm' || p === 'fd')) return true;
        return false;
      });
      if (conflicting) {
        setBlockModal({
          instructorName: targetName || targetInstructorId,
          period: targetPeriod,
          existingBooking: conflicting,
        });
        return; // don't write — chip snaps back automatically
      }
    }

    // Optimistic update — move chip locally
    const fromId = dragData.fromInstructorId;
    const fromName = memberNameMap[fromId] || 'Unassigned';
    const clientName = dragData.booking?.requesterName || 'Booking';
    setBookings(prev => prev.map(b => b.id === bookingId ? { ...b, instructorId: targetInstructorId, assignmentSource: isCollectiveTarget ? 'collective_pull_in' : 'admin_reassign' } : b));

    try {
      await assignInstructorToBooking(db, {
        bookingId,
        instructorId: targetInstructorId,
        instructorName: targetName,
        assignedByUserId: userData?.uid || '',
        assignedByName: userData?.name || '',
        userData,
        previousInstructorId: fromId || '',
        assignmentSource: isCollectiveTarget ? 'collective_pull_in' : 'admin_reassign',
      });
      await reloadBookings();
      // Show undo toast
      if (undoTimer.current) clearTimeout(undoTimer.current);
      setLastReassign({ bookingId, fromId, fromName, toId: targetInstructorId, toName: targetName, clientName });
      undoTimer.current = setTimeout(() => setLastReassign(null), 8000);
    } catch (err) {
      if (bookingsBeforeDrag.current) setBookings(bookingsBeforeDrag.current);
      setDragError({ bookingId, message: err.message || 'Reassignment failed' });
      setTimeout(() => setDragError(null), 4000);
    }
  }, [memberNameMap, members, userData, reloadBookings, dailySchedule, readOnly, selectedSeasonId]);

  if (loading) return <div style={{ padding: '60px 0', textAlign: 'center', color: V.t3, fontSize: 13, fontFamily: F }}>Loading bookings data...</div>;

  return (
    <div style={{ fontFamily: F }}>

      {/* ── PAGE HEADER ── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20, gap: 16, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 600, color: V.t1, letterSpacing: '-0.03em' }}>SAS team schedule</div>
          <div style={{ fontSize: 13, color: V.t2, marginTop: 3 }}>{fmtDayHeader(selectedDate)}</div>
        </div>
        {/* §12 hosted mode: the workspace already provides real Week/Day/Export-equivalents,
            so do NOT render these inert duplicate controls. Standalone keeps them. */}
        {!external && (
          <div style={{ display: 'flex', gap: 8 }}>
            <Btn label="Week" /><Btn label="Day" /><Btn label="Export" ghost />
          </div>
        )}
      </div>

      {/* ── STAT STRIP (Pass 58F5 §13) — operationally useful SAS scheduling truth for the selected
          day, not financial follow-up or stale Collective metadata. */}
      {(() => {
        const selLessons = calData.lessonsByDate?.[selectedDateKey] || 0;
        const selUnassigned = calData.unassignedByDate?.[selectedDateKey] || 0;
        const selAssigned = Math.max(0, selLessons - selUnassigned);
        const selRdo = calData.rdoByDate?.[selectedDateKey] || 0;
        const selHolds = calData.holdsByDate?.[selectedDateKey] || 0;
        return (
          <div className="m2" style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 10, marginBottom: 20 }}>
            <StatCard label={`Lessons · ${fmtShortDate(selectedDate)}`} value={selLessons} sub={selLessons === 0 ? 'No lessons this day' : 'client lessons'} color={V.t1} />
            {/* Pass 59B2 — the CALENDAR is an operational scheduling surface, so its headcount is the
                SCHEDULE SUPPLY (instructors represented in the connected grid source; the same population the
                capacity/utilisation denominator uses), NOT the full teaching roster. Derived mechanically —
                becomes 17 automatically if a rostered teacher (e.g. Sam Moorhouse) gains a grid block. The full
                17-person teaching roster is the people-fact shown on Overview/Team. */}
            <StatCard label="Scheduled instructors" value={selectedSeasonId === currentSeasonId ? (calData.scheduleSupplyCount || 0) : (calData.internalCount || 0)} sub="in connected schedule" color={V.t1} />
            <StatCard label="Assigned" value={selAssigned} sub="has an instructor" color={V.green} />
            <StatCard label="Holds" value={selHolds} sub={selHolds > 0 ? 'instructors committed' : 'no provisional holds'} color={selHolds > 0 ? V.purple : V.t3} />
            <StatCard label="Unassigned / RDO" value={selUnassigned} sub={selRdo > 0 ? `${selRdo} RDO` : (selUnassigned > 0 ? 'needs an instructor' : 'all assigned')} color={selUnassigned > 0 ? V.amber : V.t3} />
          </div>
        );
      })()}

      {/* ── LEGEND ── */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center', marginBottom: 16, padding: '10px 14px', background: V.surface, border: `1px solid ${V.border}`, borderRadius: 8, boxShadow: V.shadow }}>
        <span style={{ fontSize: 11, color: V.t3, fontWeight: 500 }}>Booking status:</span>
        <LegendDot color={CHIP_COLORS.green.bg} border={CHIP_COLORS.green.c} label="Paid" />
        {/* §6 amber is ONLY positive not-paid evidence (genuine); legacy/missing payment is the
            neutral gray "Payment not recorded" — never amber "debt". */}
        <LegendDot color={CHIP_COLORS.amber.bg} border={CHIP_COLORS.amber.c} label="Not paid (confirmed)" />
        <LegendDot color={CHIP_COLORS.blue.bg} border={CHIP_COLORS.blue.c} label="Deposit / pending / confirmed" />
        <LegendDot color={CHIP_COLORS.red.bg} border={CHIP_COLORS.red.c} label="Cancellation" />
        <LegendDot color={CHIP_COLORS.gray.bg} border={CHIP_COLORS.gray.c} label="Payment not recorded / day off" />
      </div>

      {/* ── INLINE CALENDAR ── */}
      <div style={{ background: V.surface, border: `1px solid ${V.border}`, borderRadius: 12, marginBottom: 16, overflow: 'hidden', boxShadow: V.shadow }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderBottom: `1px solid ${V.border}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: V.t1 }}>{monthLabel(calYear, calMonth)}</span>
            {calData.monthStats.peakDay && (
              <span style={{ fontSize: 9, fontFamily: FM, background: V.greenBg, color: V.greenTxt, border: `1px solid ${V.green}33`, borderRadius: 12, padding: '2px 8px' }}>
                Peak: {shortMonthLabel(calMonth)} {calData.monthStats.peakDay} · {calData.monthStats.peakPct}%
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            <button onClick={() => setShowCalDetail(p => !p)} style={{ fontSize: 10, fontFamily: F, color: V.t3, background: 'none', border: `1px solid ${V.border}`, borderRadius: 4, padding: '2px 8px', cursor: 'pointer' }}>{showCalDetail ? 'Hide detail' : 'Show detail'}</button>
            <CalNavBtn label="◁" onClick={() => shiftCalMonth(-1)} />
            <CalNavBtn label="▷" onClick={() => shiftCalMonth(1)} />
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: showCalDetail ? '1fr 280px' : '1fr', gap: 0 }}>
          <CompactMonth year={calYear} month={calMonth} calData={calData} selectedDateKey={selectedDateKey} onDayClick={handleCalDayClick} timeZone={tz} />
          {showCalDetail && <CalDetailPanel dateKey={selectedDateKey} calData={calData} onViewDay={onViewDay} timeZone={tz} />}
        </div>
        <div style={{ display: 'flex', gap: 12, padding: '8px 14px', borderTop: `1px solid ${V.border}`, alignItems: 'center', flexWrap: 'wrap', background: V.s2 }}>
          <span style={{ fontSize: 9, fontFamily: FM, color: V.t3, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Util:</span>
          {[[UTIL_COLORS.low, '1–25%'], [UTIL_COLORS.mid, '25–60%'], [UTIL_COLORS.high, '60–85%'], [UTIL_COLORS.peak, '85%+']].map(([c, l]) => (
            <div key={l} style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 10, color: V.t3 }}><div style={{ width: 20, height: 4, borderRadius: 2, background: c }} />{l}</div>
          ))}
        </div>
      </div>

      {/* Pass 58G2 — on mobile these desktop DnD/attention/load panels are HIDDEN (preserved for desktop);
          the phone uses the dedicated Day/Week/Unassigned views. A clear action enters Day for the selected date. */}
      {onViewDay ? (
        <button className="m-showblock" onClick={() => onViewDay(selectedDateKey)} style={{ width: '100%', margin: '4px 0 16px', padding: '12px', borderRadius: 10, border: `1px solid ${V.borderStrong}`, background: V.surface, color: V.t1, fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: F }}>View selected day →</button>
      ) : null}
      <div className="m-hide">
      {/* ── SAS SCHEDULE PANEL (Pass 58F5 §12: the Collective panel is removed until a separate live
          Collective source is connected — no pretending a disconnected source is live) ── */}
      <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 12, marginBottom: 16 }}>

          {/* LEFT: SAS Master */}
          <div style={{ background: V.surface, border: `1px solid ${V.border}`, borderRadius: 16, boxShadow: V.shadow, display: 'flex', flexDirection: 'column' }}>
            <PanelHead title="SAS master schedule" sub="You assign bookings to these instructors" badge="Hourly · admin-controlled" badgeColor="blue" />
            <DateNav date={selectedDate} onShift={shiftDate} util={`${internalBooked} / ${sasTeachingSchedule.length} slots · ${selectedDayUtilPct}%`} />

            {/* Unassigned queue — §13 static in historical read-only mode (no drag affordance). */}
            {unassignedBookings.length > 0 && (
              <div style={{ padding: '6px 16px', background: V.amberBg, borderBottom: `1px solid ${V.border}` }}>
                <div style={{ fontSize: 10, fontWeight: 600, color: V.amberTxt, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>{readOnly ? 'Unassigned' : 'Unassigned · drag to assign'}</div>
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  {unassignedBookings.map(b => (readOnly
                    ? <BkChipStatic key={b.id} booking={b} fd={classifyBookingPeriod(b) === 'fd'} />
                    : <DraggableBkChip key={b.id} booking={b} fromInstructorId="" fd={classifyBookingPeriod(b) === 'fd'} dragError={dragError} />))}
                </div>
              </div>
            )}

            <AMPMHeader />
            <div style={{ flex: 1 }}>
              {internalSchedule.map(row => <InstRowAMPM key={row.instructorId} row={row} readOnly={readOnly} onClickName={onSelectInstructor} activeDrag={activeDrag} dragError={dragError} />)}
              {/* §1 current teaching staff with no commercial category set — visible, not "School". */}
              {otherTeachingSchedule.length > 0 && (
                <div style={{ padding: '5px 16px', background: V.s2, borderTop: `1px solid ${V.border}`, borderBottom: `1px solid ${V.border}`, fontSize: 10, fontWeight: 600, color: V.t3, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Other current teaching staff · category not set</div>
              )}
              {otherTeachingSchedule.map(row => <InstRowAMPM key={row.instructorId} row={row} readOnly={readOnly} onClickName={onSelectInstructor} activeDrag={activeDrag} dragError={dragError} />)}
              {unassigned.map(row => <InstRowAMPM key={row.instructorId} row={row} readOnly={readOnly} activeDrag={activeDrag} dragError={dragError} />)}
              {internalSchedule.length === 0 && otherTeachingSchedule.length === 0 && unassigned.length === 0 && <div style={{ padding: '24px 16px', textAlign: 'center', fontSize: 12, color: V.t3 }}>No internal bookings for this date</div>}
            </div>
            <PanelFooter>
              {/* §1 this counts teaching staff NOT currently on a client lesson — they may still
                  be doing driving/admin/etc., so it is NOT a claim of operational availability. */}
              <span style={{ fontSize: 10, color: V.t3, marginLeft: 'auto' }}>{internalFree} not on a lesson today</span>
            </PanelFooter>
          </div>

        </div>

        {/* Drag overlay — ghost chip following cursor */}
        <DragOverlay dropAnimation={null}>
          {activeDrag && <BkChipGhost booking={activeDrag.booking} fd={activeDrag.fromPeriod === 'fd'} />}
        </DragOverlay>
      </DndContext>

      {/* ── BOTTOM ROW ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Card title="Needs attention">
          {attentionQueue.length > 0 ? attentionQueue.slice(0, 6).map(item => <AttnRow key={item.id} item={item} />) : (
            <div style={{ padding: '24px 16px', textAlign: 'center', fontSize: 12, color: V.t3 }}>All clear — no items need attention</div>
          )}
        </Card>
        {/* §1 this panel shows lesson LOAD (client lessons per teaching person), not true
            free/busy availability — named accordingly so it makes no claim the data can't support. */}
        <Card title="Lesson load — selected week">
          {/* Pass 58F5 §12 — SAS team only; the Collective weekly load is removed until a live Collective source connects. */}
          <AvailSection label="SAS team — lessons this week" items={weeklyUtil.filter(i => i.pool !== 'collective')} />
        </Card>
      </div>
      </div>

      {/* Pull-in modal */}
      {pullInTarget && <PullInModal target={pullInTarget} bookings={unassignedBookings} selectedId={pullInBookingId} status={pullInStatus}
        onSelect={setPullInBookingId} onConfirm={handlePullIn} onClose={() => { setPullInTarget(null); setPullInBookingId(''); setPullInStatus(''); }} />}

      {/* Drag error toast */}
      {dragError && (
        <div style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', background: V.redBg, border: `1px solid ${V.red}30`, color: V.redTxt, padding: '10px 20px', borderRadius: 10, fontSize: 12, fontFamily: F, boxShadow: '0 4px 16px rgba(0,0,0,0.1)', zIndex: 600 }}>
          {dragError.message}
        </div>
      )}

      {/* Undo reassignment toast */}
      {lastReassign && (
        <div style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', background: V.surface, border: `1px solid ${V.border}`, borderRadius: 10, padding: '10px 16px', fontSize: 12, fontFamily: F, boxShadow: '0 4px 16px rgba(0,0,0,0.12)', zIndex: 600, display: 'flex', alignItems: 'center', gap: 12, maxWidth: 440 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <span style={{ color: V.t1, fontWeight: 500 }}>Moved </span>
            <span style={{ color: V.green, fontWeight: 600 }}>{lastReassign.clientName}</span>
            <span style={{ color: V.t2 }}> → {lastReassign.toName || 'instructor'}</span>
          </div>
          <button onClick={async () => {
            if (readOnly) return; // §3 defensive — no undo write in read-only mode
            const r = lastReassign;
            setLastReassign(null);
            if (undoTimer.current) clearTimeout(undoTimer.current);
            try {
              await assignInstructorToBooking(db, {
                bookingId: r.bookingId,
                instructorId: r.fromId || '',
                instructorName: r.fromName || '',
                assignedByUserId: userData?.uid || '',
                assignedByName: userData?.name || '',
                userData,
                assignmentSource: 'admin_reassign',
              });
              await reloadBookings();
            } catch (err) {
              setDragError({ bookingId: r.bookingId, message: `Undo failed: ${err.message}` });
              setTimeout(() => setDragError(null), 4000);
            }
          }} style={{ background: V.t1, color: '#fff', border: 'none', borderRadius: 6, padding: '6px 14px', fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: F, whiteSpace: 'nowrap' }}>
            Undo
          </button>
          <button onClick={() => { setLastReassign(null); if (undoTimer.current) clearTimeout(undoTimer.current); }} style={{ background: 'none', border: 'none', color: V.t3, cursor: 'pointer', fontSize: 14, padding: '2px 4px', lineHeight: 1 }}>✕</button>
        </div>
      )}

      {/* Block modal — shown when dropping onto an occupied slot */}
      {blockModal && <BlockModal blockModal={blockModal} onClose={() => setBlockModal(null)} />}
    </div>
  );
}

// ── Droppable AM/PM cell ───────────────────────────────────────────────────

function DroppableCell({ id, activeDrag, children, style: extraStyle }) {
  const { isOver, setNodeRef } = useDroppable({ id });
  const isSameInstructor = activeDrag && String(id).split('::')[0] === activeDrag.fromInstructorId;
  const isValid = activeDrag && !isSameInstructor;

  let bg = 'transparent';
  let borderStyle = 'none';
  if (activeDrag) {
    if (isOver && isValid) { bg = 'rgba(29,158,117,0.06)'; borderStyle = '1.5px dashed #1D9E75'; }
    else if (isOver && isSameInstructor) { bg = 'rgba(220,38,38,0.04)'; borderStyle = '1.5px dashed #DC2626'; }
    else if (isValid) { bg = 'rgba(29,158,117,0.02)'; }
  }

  return (
    <div ref={setNodeRef} style={{ padding: '4px 6px', display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center', minHeight: 38, background: bg, outline: borderStyle, outlineOffset: '-1.5px', transition: 'background 0.15s', ...extraStyle }}>
      {children}
    </div>
  );
}

// ── Draggable booking chip ─────────────────────────────────────────────────

function DraggableBkChip({ booking, fromInstructorId, fd, dragError }) {
  // Pass 59B3 — a schedule-grid hold is a source-owned commitment, not a reassignable lesson → not draggable.
  const isDraggable = !isGridHold(booking) && !['canceled', 'completed', 'no_show'].includes(booking.status);
  const period = classifyBookingPeriod(booking);
  const windowBadge = getTimeWindowBadge(booking, period);
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `drag-${booking.id}${fd ? '-fd' : ''}`,
    data: { booking, bookingId: booking.id, fromInstructorId, fromPeriod: period },
    disabled: !isDraggable,
  });

  const { label, color } = getBookingChipStatus(booking);
  const isPulledIn = booking.assignmentSource === 'collective_pull_in' || (!!booking.previousInstructorId && !!booking.reassignedAt);
  const cs = isPulledIn ? CHIP_COLORS.purple : (CHIP_COLORS[color] || CHIP_COLORS.gray);
  const chipName = isGridHold(booking) ? holdChipName(booking) : (booking.requesterName || booking.serviceNameSnapshot || 'Client');
  const time = fmtTime(booking.startAt);
  const chipText = isGridHold(booking) ? (fd ? `Hold · ${chipName}` : chipName) : (fd ? `FD · ${chipName}` : `${time} ${chipName}`);
  const hasError = dragError?.bookingId === booking.id;

  if (isDragging) {
    return <span style={{ fontSize: 10, borderRadius: 4, padding: '2px 7px', background: V.s3, color: V.t3, fontStyle: 'italic', display: 'inline-block', border: `1px dashed ${V.border}` }}>dropping here...</span>;
  }

  return (
    <span ref={setNodeRef} {...attributes} {...listeners} title={`${label}${isPulledIn ? ' — pulled in' : ''}${isDraggable ? ' · drag to reassign' : ''}`} style={{
      fontSize: 10, borderRadius: 4, padding: '2px 2px 2px 0', lineHeight: 1.4,
      whiteSpace: 'normal', maxWidth: 200,
      fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 0, flexWrap: 'wrap',
      background: hasError ? V.redBg : cs.bg, color: hasError ? V.redTxt : cs.c,
      border: hasError ? `1.5px solid ${V.red}` : isPulledIn ? cs.border : 'none',
      textDecoration: color === 'red' ? 'line-through' : 'none',
      cursor: isDraggable ? 'grab' : 'default',
      touchAction: 'none',
    }}>
      {isDraggable && (
        <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 1, padding: '2px 3px 2px 4px', opacity: 0.35, flexShrink: 0 }}>
          <span style={{ display: 'flex', gap: 1.5 }}><span style={{ width: 2, height: 2, borderRadius: '50%', background: 'currentColor' }} /><span style={{ width: 2, height: 2, borderRadius: '50%', background: 'currentColor' }} /></span>
          <span style={{ display: 'flex', gap: 1.5 }}><span style={{ width: 2, height: 2, borderRadius: '50%', background: 'currentColor' }} /><span style={{ width: 2, height: 2, borderRadius: '50%', background: 'currentColor' }} /></span>
          <span style={{ display: 'flex', gap: 1.5 }}><span style={{ width: 2, height: 2, borderRadius: '50%', background: 'currentColor' }} /><span style={{ width: 2, height: 2, borderRadius: '50%', background: 'currentColor' }} /></span>
        </span>
      )}
      <span style={{ padding: '0 5px' }}>{chipText}</span>
      {windowBadge && <span style={{ fontSize: 9, background: '#FEF3C7', color: '#92400E', padding: '1px 5px', borderRadius: 3, marginLeft: 2, flexShrink: 0 }}>{windowBadge}</span>}
    </span>
  );
}

// Ghost chip for DragOverlay
function BkChipGhost({ booking, fd }) {
  const { color } = getBookingChipStatus(booking);
  const cs = CHIP_COLORS[color] || CHIP_COLORS.gray;
  const chipName = booking.requesterName || booking.serviceNameSnapshot || 'Client';
  const chipText = fd ? `FD · ${chipName}` : `${fmtTime(booking.startAt)} ${chipName}`;
  return (
    <span style={{ fontSize: 10, borderRadius: 4, padding: '3px 8px', fontWeight: 500, background: cs.bg, color: cs.c, border: `1.5px solid ${cs.c}40`, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', whiteSpace: 'nowrap', cursor: 'grabbing' }}>
      {chipText}
    </span>
  );
}

// ── Instructor rows with droppable cells ───────────────────────────────────

function splitAMPM(bookings) {
  const am = []; const pm = []; const fd = [];
  for (const b of bookings) {
    const period = classifyBookingPeriod(b);
    if (period === 'fd') fd.push(b);
    else if (period === 'pm') pm.push(b);
    else am.push(b);
  }
  return { am, pm, fd };
}

function InstRowAMPM({ row, onClickName, activeDrag, dragError, readOnly }) {
  // Pass 59B3 — a COVERED grid hold is already shown by its real client booking; never render it twice in the
  // Master Schedule row. Standalone holds DO occupy their slot.
  const rowBookings = row.bookings.filter((b) => !isCoveredGridHold(b));
  const { am, pm, fd } = splitAMPM(rowBookings);
  const hasAny = rowBookings.length > 0;
  const instId = row.instructorId;
  // §13 historical read-only → static chips (no drag handle, cursor, or title) and inert cells.
  const Chip = ({ b, fd: full }) => (readOnly
    ? <BkChipStatic key={full ? `${b.id}-s` : b.id} booking={b} fd={full} />
    : <DraggableBkChip key={full ? `${b.id}-d` : b.id} booking={b} fromInstructorId={instId} fd={full} dragError={dragError} />);
  const Cell = ({ id, children, style }) => (readOnly
    ? <div style={{ padding: '4px 8px', display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'center', ...style }}>{children}</div>
    : <DroppableCell id={id} activeDrag={activeDrag} style={style}>{children}</DroppableCell>);

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr 1fr', borderBottom: `1px solid ${V.border}`, minHeight: 38 }}
      onMouseEnter={e => { if (!activeDrag) e.currentTarget.style.background = V.s2; }}
      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
      <NameCell name={row.instructorName} meta={`${row.discipline}${row.rate ? ` · ${row.rate}` : ''}`} onClick={onClickName ? () => onClickName(instId, row.instructorName) : null} />
      <Cell id={`${instId}::am`} style={{ borderRight: `1px solid ${V.border}` }}>
        {fd.map(b => <Chip key={`${b.id}-fd`} b={b} fd />)}
        {am.map(b => <Chip key={b.id} b={b} />)}
        {!hasAny && <span style={{ fontSize: 10, color: V.t3, fontStyle: 'italic' }}>—</span>}
      </Cell>
      <Cell id={`${instId}::pm`}>
        {fd.map(b => <Chip key={`${b.id}-pmfd`} b={b} fd />)}
        {pm.map(b => <Chip key={b.id} b={b} />)}
      </Cell>
    </div>
  );
}

// Non-draggable chip for collective's own bookings
function BkChipStatic({ booking, fd }) {
  const { label, color } = getBookingChipStatus(booking);
  const period = classifyBookingPeriod(booking);
  const windowBadge = getTimeWindowBadge(booking, period);
  const isPulledIn = booking.assignmentSource === 'collective_pull_in' || (!!booking.previousInstructorId && !!booking.reassignedAt);
  const cs = isPulledIn ? CHIP_COLORS.purple : (CHIP_COLORS[color] || CHIP_COLORS.gray);
  const chipName = isGridHold(booking) ? holdChipName(booking) : (booking.requesterName || booking.serviceNameSnapshot || 'Client');
  const chipText = isGridHold(booking) ? (fd ? `Hold · ${chipName}` : chipName) : (fd ? `FD · ${chipName}` : `${fmtTime(booking.startAt)} ${chipName}`);
  return (
    <span title={label} style={{ fontSize: 10, borderRadius: 4, padding: '2px 7px', lineHeight: 1.4, whiteSpace: 'normal', maxWidth: 200, fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 0, flexWrap: 'wrap', background: cs.bg, color: cs.c, border: isPulledIn ? cs.border : 'none', textDecoration: color === 'red' ? 'line-through' : 'none' }}>
      {chipText}
      {windowBadge && <span style={{ fontSize: 9, background: '#FEF3C7', color: '#92400E', padding: '1px 5px', borderRadius: 3, marginLeft: 2, flexShrink: 0 }}>{windowBadge}</span>}
    </span>
  );
}

// ── Shared sub-components (calendar, panels, etc.) ─────────────────────────

function AMPMHeader({ purple }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr 1fr', borderBottom: `1px solid ${purple ? V.purpleBorder : V.border}`, background: purple ? '#FAF8FF' : V.s3 }}>
      <div />
      <div style={{ fontSize: 10, fontFamily: FM, color: V.t3, textAlign: 'center', padding: '4px 0', borderLeft: `1px solid ${V.border}` }}>AM · 09:00–12:00</div>
      <div style={{ fontSize: 10, fontFamily: FM, color: V.t3, textAlign: 'center', padding: '4px 0', borderLeft: `1px solid ${V.border}` }}>PM · 13:00–16:00</div>
    </div>
  );
}

function NameCell({ name, meta, purple, onClick, action }) {
  return (
    <div onClick={onClick} style={{ padding: '6px 12px', borderRight: `1px solid ${V.border}`, background: purple ? '#FAF8FF' : V.s2, display: 'flex', flexDirection: 'column', justifyContent: 'center', cursor: onClick ? 'pointer' : 'default' }}>
      <div style={{ fontSize: 11, fontWeight: 500, color: purple ? V.purple : V.t1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'flex', alignItems: 'center', gap: 3 }}>
        {name}{onClick && <span style={{ fontSize: 9, opacity: 0.4 }}>↗</span>}
      </div>
      <div style={{ fontSize: 10, color: V.t3, fontFamily: FM, marginTop: 1 }}>{meta}</div>
      {action}
    </div>
  );
}

function CompactMonth({ year, month, calData, selectedDateKey, onDayClick, timeZone = LEGACY_OPS_TIMEZONE }) {
  // Pass 58F5 — the month calendar shows real booking DEMAND (lessons / unassigned / RDO); utilisation
  // is a subtle secondary bar, no longer the only evidence a day has bookings.
  const { utilByDate, totalSlots, lessonsByDate = {}, unassignedByDate = {}, rdoByDate = {}, holdsByDate = {} } = calData;
  const firstDow = new Date(year, month, 1).getDay();
  const offset = firstDow === 0 ? 6 : firstDow - 1;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  // §9 "today" highlight uses the ORG-timezone calendar day (grid cells are month/day integers
  // that are timezone-agnostic, so only the today-key needs the zone).
  const todayKey = zonedDateKey(new Date(), timeZone);
  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', background: V.s2, borderBottom: `1px solid ${V.border}` }}>
        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d, i) => (
          <div key={i} style={{ fontSize: 10, fontWeight: 600, fontFamily: FM, color: i >= 5 ? V.purple : V.t3, textAlign: 'center', padding: '5px 0', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{d}</div>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)' }}>
        {Array.from({ length: offset }, (_, i) => <div key={`e${i}`} style={{ minHeight: 64, background: V.s2, borderRight: `1px solid ${V.border}`, borderBottom: `1px solid ${V.border}` }} />)}
        {Array.from({ length: daysInMonth }, (_, i) => {
          const day = i + 1;
          const key = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          const lessons = lessonsByDate[key] || 0;
          const unassigned = unassignedByDate[key] || 0;
          const rdo = rdoByDate[key] || 0;
          const holds = holdsByDate[key] || 0;
          const booked = utilByDate[key] || 0;
          const pct = totalSlots > 0 ? Math.round(booked / totalSlots * 100) : 0;
          const isToday = key === todayKey;
          const isSelected = key === selectedDateKey;
          const uClass = utilBarClass(pct);
          return (
            <div key={key} onClick={() => onDayClick(key)} style={{ minHeight: 64, padding: '4px 6px', cursor: 'pointer', background: isSelected ? V.greenBg : V.surface, outline: isSelected ? `2px solid ${V.green}` : 'none', outlineOffset: '-2px', borderRight: `1px solid ${V.border}`, borderBottom: `1px solid ${V.border}`, transition: 'background 0.1s' }}
              onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = V.s2; }}
              onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = isSelected ? V.greenBg : V.surface; }}>
              <div style={{ fontSize: 11, fontWeight: 600, fontFamily: FM, color: isToday ? '#fff' : isSelected ? V.greenTxt : V.t2, width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', background: isToday ? V.green : 'none', marginBottom: 2 }}>{day}</div>
              {/* Utilisation stays a subtle secondary bar. */}
              {booked > 0 && <div style={{ height: 3, background: V.s3, borderRadius: 2, overflow: 'hidden', marginBottom: 3 }}><div style={{ height: '100%', borderRadius: 2, width: `${pct}%`, background: UTIL_COLORS[uClass] }} /></div>}
              {/* Real booking demand \u2014 the primary evidence bookings exist. */}
              {lessons > 0 && <div style={{ fontSize: 10, fontWeight: 700, fontFamily: FM, color: V.t1, lineHeight: 1.25 }}>{lessons} {lessons === 1 ? 'lesson' : 'lessons'}</div>}
              {/* Pass 59B — provisional operational holds (instructors committed on the SAS schedule grid, not
                  yet a full client booking). Distinct from lessons; never merged into the lesson count. */}
              {holds > 0 && <div style={{ fontSize: 9, fontWeight: 700, color: V.purple, lineHeight: 1.3 }}>{holds} {holds === 1 ? 'hold' : 'holds'}</div>}
              {unassigned > 0 && <div style={{ fontSize: 9, fontWeight: 700, color: V.amber, lineHeight: 1.3 }}>{unassigned} unassigned</div>}
              {rdo > 0 && <div style={{ fontSize: 9, color: V.t3, lineHeight: 1.3 }}>{rdo} RDO</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CalNavBtn({ label, onClick }) {
  return <button onClick={onClick} style={{ width: 24, height: 24, borderRadius: 4, border: `1px solid ${V.border}`, background: V.surface, color: V.t2, cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: F }}>{label}</button>;
}

function CalDetailPanel({ dateKey, calData, onViewDay, timeZone = LEGACY_OPS_TIMEZONE }) {
  if (!dateKey || !calData) return <div style={{ borderLeft: `1px solid ${V.border}`, padding: 16, fontSize: 11, color: V.t3 }}>Select a day</div>;
  // §9 anchor at org-local noon and format in the org timezone so the header day matches the calendar bucket.
  const d = wallClockToInstant(dateKey, '12:00', timeZone);
  const lessons = calData.lessonsByDate?.[dateKey] || 0;
  const unassigned = calData.unassignedByDate?.[dateKey] || 0;
  const assigned = Math.max(0, lessons - unassigned);
  const rdo = calData.rdoByDate?.[dateKey] || 0;
  const holds = calData.holdsByDate?.[dateKey] || 0;
  const data = calData.bookingsByDate[dateKey];
  const empty = lessons === 0 && rdo === 0 && holds === 0;
  return (
    <div style={{ borderLeft: `1px solid ${V.border}`, display: 'flex', flexDirection: 'column', maxHeight: 420 }}>
      <div style={{ padding: '10px 14px', borderBottom: `1px solid ${V.border}`, background: V.s2 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: V.t1 }}>{d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone })}</div>
        <div style={{ fontSize: 11, color: V.t2, marginTop: 3, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {lessons > 0 && <span><strong style={{ color: V.t1 }}>{lessons}</strong> {lessons === 1 ? 'lesson' : 'lessons'}</span>}
          {assigned > 0 && <span style={{ color: V.green }}>{assigned} assigned</span>}
          {holds > 0 && <span style={{ color: V.purple, fontWeight: 700 }}>{holds} {holds === 1 ? 'hold' : 'holds'}</span>}
          {unassigned > 0 && <span style={{ color: V.amber, fontWeight: 700 }}>{unassigned} unassigned</span>}
          {rdo > 0 && <span style={{ color: V.t3 }}>{rdo} RDO</span>}
          {empty && <span style={{ color: V.t3 }}>No bookings this day</span>}
        </div>
      </div>
      <div style={{ overflowY: 'auto', flex: 1 }}>
        {data?.sas?.slice(0, 8).map((b, i) => <div key={i} style={{ padding: '5px 14px', borderBottom: `1px solid ${V.border}`, fontSize: 11 }}><div style={{ fontWeight: 500, color: V.t1 }}>{b.inst}</div><div style={{ color: V.t3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{b.detail}</div></div>)}
        {data?.sas?.length > 8 && <div style={{ padding: '4px 14px', fontSize: 10, color: V.t3 }}>+{data.sas.length - 8} more</div>}
      </div>
      {!empty && onViewDay && (
        <button onClick={() => onViewDay(dateKey)} style={{ margin: 10, padding: '8px 12px', borderRadius: 8, border: `1px solid ${V.borderStrong}`, background: V.surface, color: V.t1, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: F }}>View day schedule →</button>
      )}
    </div>
  );
}

function StatCard({ label, value, sub, color }) {
  return <div style={{ background: V.surface, border: `1px solid ${V.border}`, borderRadius: 12, padding: '14px 16px', boxShadow: V.shadow }}><div style={{ fontSize: 10, color: V.t3, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 500, marginBottom: 6 }}>{label}</div><div style={{ fontSize: 24, fontWeight: 600, color: color || V.t1, lineHeight: 1, letterSpacing: '-0.03em', fontFamily: FM }}>{value}</div><div style={{ fontSize: 11, color: V.t3, marginTop: 4 }}>{sub}</div></div>;
}
function LegendDot({ color, border, label }) {
  return <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: V.t2 }}><div style={{ width: 8, height: 8, borderRadius: '50%', background: color, border: `1px solid ${border}`, flexShrink: 0 }} />{label}</div>;
}
function Btn({ label, ghost, small, purple, onClick }) {
  return <button onClick={onClick} style={{ fontSize: small ? 11 : 12, padding: small ? '4px 10px' : '6px 14px', borderRadius: 8, border: `1px solid ${purple ? V.purpleBorder : ghost ? 'transparent' : V.borderStrong}`, background: ghost ? 'transparent' : V.surface, color: purple ? V.purpleTxt : V.t1, cursor: 'pointer', fontFamily: F, fontWeight: 500, whiteSpace: 'nowrap' }}>{label}</button>;
}
function PanelHead({ title, sub, badge, badgeColor }) {
  const bc = badgeColor === 'purple' ? { bg: V.purpleSoft, c: V.purpleTxt } : { bg: V.blueBg, c: V.blueTxt };
  return <div style={{ padding: '14px 16px', borderBottom: `1px solid ${V.border}`, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}><div><div style={{ fontSize: 14, fontWeight: 600, color: V.t1 }}>{title}</div><div style={{ fontSize: 11, color: V.t3, marginTop: 2 }}>{sub}</div></div><span style={{ fontSize: 10, padding: '3px 9px', borderRadius: 20, fontWeight: 500, background: bc.bg, color: bc.c }}>{badge}</span></div>;
}
function DateNav({ date, onShift, util, purple }) {
  return <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px', background: V.s2, borderBottom: `1px solid ${V.border}` }}><button onClick={() => onShift(-1)} style={{ background: 'none', border: `1px solid ${V.border}`, borderRadius: 4, width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 12, color: V.t2 }}>◁</button><span style={{ fontSize: 12, fontWeight: 500, color: V.t1 }}>{fmtDayHeader(date)}</span><button onClick={() => onShift(1)} style={{ background: 'none', border: `1px solid ${V.border}`, borderRadius: 4, width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 12, color: V.t2 }}>▷</button><span style={{ fontSize: 11, color: V.t3, marginLeft: 'auto' }}>{util && <strong style={{ color: purple ? V.purple : V.green }}>{util.split(' ')[0]}</strong>} {util && util.split(' ').slice(1).join(' ')}</span></div>;
}
function PanelFooter({ children, purple }) {
  return <div style={{ padding: '8px 16px', borderTop: `1px solid ${purple ? V.purpleBorder : V.border}`, background: purple ? '#FAF8FF' : V.s2, display: 'flex', gap: 8, alignItems: 'center' }}>{children}</div>;
}
function Card({ title, children }) {
  return <div style={{ background: V.surface, border: `1px solid ${V.border}`, borderRadius: 16, boxShadow: V.shadow, overflow: 'hidden' }}><div style={{ padding: '12px 16px', borderBottom: `1px solid ${V.border}`, fontSize: 13, fontWeight: 600, color: V.t1 }}>{title}</div>{children}</div>;
}
function AttnRow({ item }) {
  const badgeMap = { unpaid: { label: 'Not paid', bg: V.amberBg, c: V.amberTxt }, pending_request: { label: 'Pending', bg: V.blueBg, c: V.blueTxt }, needs_confirmation: { label: 'Confirm', bg: V.blueBg, c: V.blueTxt }, needs_assignment: { label: 'Assign', bg: V.redBg, c: V.redTxt }, change_request: { label: 'Change', bg: V.amberBg, c: V.amberTxt }, follow_up: { label: 'Follow up', bg: V.greenBg, c: V.greenTxt }, sync_error: { label: 'Sync', bg: V.grayBg, c: V.grayTxt } };
  const badge = badgeMap[item.type] || badgeMap.sync_error;
  const actionLabels = { unpaid: 'Chase payment →', pending_request: 'Decide route →', needs_assignment: 'Reassign →', change_request: 'Confirm →', follow_up: 'Schedule →', needs_confirmation: 'Confirm →', sync_error: 'Fix →' };
  return <div style={{ padding: '10px 16px', borderBottom: `1px solid ${V.border}` }} onMouseEnter={e => e.currentTarget.style.background = V.s2} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}><div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8, marginBottom: 3 }}><span style={{ fontSize: 12, fontWeight: 500, color: V.t1, flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.label}</span><span style={{ fontSize: 10, padding: '3px 9px', borderRadius: 20, fontWeight: 500, background: badge.bg, color: badge.c, whiteSpace: 'nowrap' }}>{badge.label}</span></div><div style={{ fontSize: 11, color: V.t2, marginBottom: 5 }}>{item.detail}</div><button style={{ fontSize: 10, padding: '3px 10px', borderRadius: 4, border: `1px solid ${V.borderStrong}`, background: V.surface, cursor: 'pointer', fontFamily: F, color: V.t2, fontWeight: 500 }}>{actionLabels[item.type] || 'View →'}</button></div>;
}
function AvailSection({ label, items, purple }) {
  return <>
    <div style={{ fontSize: 10, fontWeight: 600, color: purple ? V.purple : V.t3, textTransform: 'uppercase', letterSpacing: '0.06em', padding: '8px 16px 4px', borderBottom: `1px solid ${V.border}`, background: purple ? '#FAF8FF' : V.s2 }}>{label}</div>
    {items.slice(0, 5).map(inst => <div key={inst.instructorId} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 16px', borderBottom: `1px solid ${V.border}` }} onMouseEnter={e => e.currentTarget.style.background = V.s2} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}><div style={{ fontSize: 11, fontWeight: 500, color: purple ? V.purple : V.t1, minWidth: 100 }}>{inst.name}</div><div style={{ fontSize: 10, color: V.t3, minWidth: 50, fontFamily: FM }}>{inst.discipline}</div><div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8 }}><div style={{ height: 5, flex: 1, background: V.s3, borderRadius: 3, overflow: 'hidden' }}><div style={{ height: '100%', borderRadius: 3, width: `${inst.pct}%`, background: purple ? V.purple : V.green }} /></div><span style={{ fontSize: 10, color: purple ? V.purple : V.t3, minWidth: 28, textAlign: 'right', fontFamily: FM }}>{inst.pct}%</span></div><span style={{ fontSize: 10, padding: '3px 9px', borderRadius: 20, fontWeight: 500, background: purple ? V.purpleSoft : V.greenBg, color: purple ? V.purpleTxt : V.greenTxt }}>{inst.bookingsThisWeek}/7</span></div>)}
    {items.length === 0 && <div style={{ padding: '16px', fontSize: 11, color: V.t3, textAlign: 'center' }}>No instructors</div>}
  </>;
}
function PullInModal({ target, bookings, selectedId, status, onSelect, onConfirm, onClose }) {
  return <div onClick={e => { if (e.target === e.currentTarget) onClose(); }} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><div style={{ background: V.surface, border: `1px solid ${V.borderStrong}`, borderRadius: 14, padding: 24, width: '100%', maxWidth: 440, fontFamily: F }}><div style={{ fontSize: 16, fontWeight: 600, color: V.t1, marginBottom: 4 }}>Pull in {target.instructorName}</div><div style={{ fontSize: 12, color: V.t3, marginBottom: 16 }}>Assign to an unassigned booking</div>{bookings.length > 0 ? <><div style={{ maxHeight: 240, overflowY: 'auto', marginBottom: 16 }}>{bookings.map(b => <div key={b.id} onClick={() => onSelect(b.id)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 10, border: `1px solid ${selectedId === b.id ? V.purpleBorder : V.border}`, background: selectedId === b.id ? V.purpleSoft : V.s2, cursor: 'pointer', marginBottom: 6 }}><div style={{ flex: 1 }}><div style={{ fontSize: 13, fontWeight: 600, color: V.t1 }}>{b.requesterName || 'Client'}</div><div style={{ fontSize: 11, color: V.t3 }}>{b.serviceNameSnapshot || 'Session'} · {fmtShortDate(b.startAt)} {fmtTime(b.startAt)}</div></div><div style={{ width: 16, height: 16, borderRadius: '50%', border: `2px solid ${selectedId === b.id ? V.purple : V.border}`, background: selectedId === b.id ? V.purple : 'transparent' }} /></div>)}</div>{status === 'success' && <div style={{ background: V.greenBg, borderRadius: 8, padding: '8px 12px', fontSize: 12, color: V.greenTxt, marginBottom: 12 }}>Assigned successfully</div>}{status?.startsWith?.('error:') && <div style={{ background: V.redBg, borderRadius: 8, padding: '8px 12px', fontSize: 12, color: V.redTxt, marginBottom: 12 }}>{status.slice(6)}</div>}<div style={{ display: 'flex', gap: 8 }}><button onClick={onClose} style={{ flex: 1, background: V.s2, border: `1px solid ${V.border}`, color: V.t2, padding: 10, borderRadius: 10, fontFamily: F, fontSize: 13, cursor: 'pointer' }}>Cancel</button><button onClick={onConfirm} disabled={!selectedId || status === 'loading' || status === 'success'} style={{ flex: 1, background: V.purple, color: '#fff', border: 'none', padding: 10, borderRadius: 10, fontFamily: F, fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: (!selectedId || status === 'loading') ? 0.5 : 1 }}>{status === 'loading' ? 'Assigning...' : 'Assign'}</button></div></> : <div style={{ padding: '20px 0', textAlign: 'center', color: V.t3, fontSize: 13 }}>No unassigned bookings available</div>}</div></div>;
}

// ── Block modal with inline booking detail ─────────────────────────────────

function BlockModal({ blockModal, onClose }) {
  const [view, setView] = useState('conflict'); // 'conflict' | 'detail'
  const [copied, setCopied] = useState(false);
  const bk = blockModal.existingBooking;
  const period = bk ? classifyBookingPeriod(bk) : null;
  const { label: statusLabel, color: statusColor } = bk ? getBookingChipStatus(bk) : { label: '—', color: 'gray' };
  const statusCs = CHIP_COLORS[statusColor] || CHIP_COLORS.gray;

  const handleCopyId = () => {
    if (bk?.id) { navigator.clipboard?.writeText(bk.id).catch(() => {}); setCopied(true); setTimeout(() => setCopied(false), 1200); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 600, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={e => { if (e.target === e.currentTarget) { onClose(); setView('conflict'); } }}>
      <div style={{ background: V.surface, border: `0.5px solid ${V.border}`, borderRadius: 12, padding: 24, width: '100%', maxWidth: 400, fontFamily: F, boxShadow: '0 8px 32px rgba(0,0,0,0.12)' }}>

        {view === 'conflict' && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
              <div style={{ width: 32, height: 32, borderRadius: '50%', background: V.redBg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, color: V.red, flexShrink: 0 }}>✕</div>
              <div style={{ fontSize: 14, fontWeight: 500, color: V.t1 }}>Cannot assign here</div>
            </div>
            <div style={{ fontSize: 12, color: V.t2, marginBottom: 12, lineHeight: 1.5 }}>
              <strong>{blockModal.instructorName}</strong> already has a{blockModal.period === 'am' ? 'n AM' : ' PM'} booking on this date.
            </div>
            {bk && (
              <div style={{ background: V.s2, border: `1px solid ${V.border}`, borderRadius: 8, padding: '8px 12px', fontSize: 11, color: V.t2, marginBottom: 14 }}>
                Existing: {fmtTime(bk.startAt)} {bk.requesterName || 'Client'} · {period === 'fd' ? 'Full day' : blockModal.period === 'am' ? 'AM' : 'PM'}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setView('detail')} style={{ flex: 1, background: V.t1, color: '#fff', border: 'none', padding: '9px 16px', borderRadius: 8, fontFamily: F, fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>
                Edit booking
              </button>
              <button onClick={() => { onClose(); setView('conflict'); }} style={{ flex: 1, background: 'transparent', border: `0.5px solid ${V.border}`, color: V.t2, padding: '9px 16px', borderRadius: 8, fontFamily: F, fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          </>
        )}

        {view === 'detail' && (
          <>
            {/* Header with back */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
              <button onClick={() => setView('conflict')} style={{ background: 'none', border: 'none', color: V.t3, cursor: 'pointer', fontSize: 12, fontFamily: F, padding: 0 }}>← Back</button>
              <div style={{ fontSize: 14, fontWeight: 500, color: V.t1 }}>{bk?.requesterName || 'Booking detail'}</div>
            </div>

            {/* Detail fields */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 16px', marginBottom: 16 }}>
              <DetailField label="Client" value={bk?.requesterName || '—'} />
              <DetailField label="Booking ref" value={bk?.bookingRequestId || bk?.id?.slice(0, 12) || '—'} mono />
              <DetailField label="Date & time" value={bk?.startAt ? `${fmtShortDate(bk.startAt)} ${fmtTime(bk.startAt)}` : '—'} />
              <DetailField label="Duration" value={bk?.serviceDurationSnapshot ? `${Math.round(bk.serviceDurationSnapshot / 60)}h (${bk.serviceDurationSnapshot}min)` : '—'} />
              <DetailField label="Lesson type" value={bk?.serviceNameSnapshot || bk?.serviceType || '—'} />
              <DetailField label="Discipline" value={bk?.discipline || '—'} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <DetailField label="Payment" value="" />
                <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 10, background: statusCs.bg, color: statusCs.c, fontWeight: 500, marginTop: 14 }}>{statusLabel}</span>
              </div>
              <DetailField label="Instructor" value={blockModal.instructorName || '—'} />
            </div>

            {/* Quick actions */}
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={handleCopyId} style={{ flex: 1, background: V.t1, color: '#fff', border: 'none', padding: '9px 16px', borderRadius: 8, fontFamily: F, fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>
                {copied ? 'Copied!' : 'Copy booking ID'}
              </button>
              <button onClick={() => { onClose(); setView('conflict'); }} style={{ flex: 1, background: 'transparent', border: `0.5px solid ${V.border}`, color: V.t2, padding: '9px 16px', borderRadius: 8, fontFamily: F, fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>
                Close
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function DetailField({ label, value, mono }) {
  return (
    <div>
      <div style={{ fontSize: 9, fontFamily: FM, color: V.t3, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 12, color: V.t1, fontFamily: mono ? FM : F }}>{value}</div>
    </div>
  );
}

export default OrgBookingsCRM;
